// ─── Creation pairing, search grammar, and list rows ─────────────────────────

import { useState, type ReactNode } from "react";
import { Chip, CloseButton, HitArea } from "./primitives";
import { StatusDot, type OperationalStatus } from "./state";

// ─── ADD (+) / TELL MIRA ─────────────────────────────────────────────────────

/**
 * A primary action, optionally beside sibling actions of equal weight.
 *
 * ── The natural-language twin is GONE (Drea, 2026-08-22) ────────────────────
 *
 * This component used to carry one, and the library's design language said it
 * was universal: "every manual creation surface (Add Person, Add Task, Add
 * Group, Add Alert) has a natural-language twin … not Mira being a separate
 * feature." Every page's `ADD` sat beside an `ASK MIRA` that opened the
 * assistant panel.
 *
 * Drea reversed it: "too much noise and not well set up." Deliberately, and
 * across the whole app rather than page by page — recorded here because a
 * reversal that is not written down gets re-added by the next person reading
 * the old principle.
 *
 * What replaces it: the ONE persistent "Ask Mira AI" bar at the bottom of
 * every screen. Mira did not become less reachable — she is on every page, in
 * a fixed place, instead of duplicated into every action row.
 *
 * `tellLabel` is therefore REQUIRED alongside `onTell` now. It used to default
 * to "Ask Mira", which is exactly how a twin could reappear by omission.
 */
export function AddOrTell({
  noun, onAdd, onTell, extra, tone = "dark", addLabel, tellLabel, addTestId,
}: {
  /** A hook for the suites. An attribute; nothing renders differently. */
  addTestId?: string;
  /** Override the generated "ADD <NOUN>" — e.g. "Capture paper entry". The
   *  pair's shape is the point, not the wording. */
  addLabel?: string;
  tellLabel?: string;
  /**
   * `light` is the BRIGHT band — the pale surface the Ask Mira bar sits on.
   *
   * The site-wide rule (Drea, 2026-08-20): small pure-navigation controls are
   * mid green; ACTIONS are this, and are meant to stand out most. Add/View
   * pairs at the top of a collection are actions, so they belong here.
   *
   * It spent a few hours on the pulse-tile green after "make it match the
   * tile-style buttons" was read as one uniform surface. That was wrong: the
   * point is not uniformity, it is that an action outranks navigation.
   */
  tone?: "dark" | "light";
  /** "Person", "Task", "Group", "Alert" — becomes `ADD PERSON`. */
  noun: string;
  onAdd: () => void;
  /**
   * Optional, and omitted rather than stubbed. Every AppFrame screen already
   * carries the Ask Mira bar, so a second entry point that did nothing was
   * worse than no second entry point — see the audit note in PROJECT_LOG.
   */
  onTell?: () => void;
  /** Optional sibling actions shown alongside, e.g. "VIEW GROUPS". */
  extra?: { label: string; onPress: () => void }[];
}) {
  const light = tone === "light";
  const buttonStyle = {
    borderRadius: "var(--nl-radius)",
    background: light ? "var(--nl-band)" : "var(--nl-tile)",
    border: "1px solid var(--nl-stroke)",
    color: "var(--nl-ink)",
    cursor: "pointer",
    minHeight: "var(--nl-hit-min)",
    /**
     * ── PREVIEW SWITCH, 2026-09-27 — not a decision ────────────────────────
     *
     * Drea wants to SEE these flat before judging them: three flat shades of
     * teal, no gloss. `--nl-inner-light` is Farndale's own token, extracted
     * faithfully, and this button is not Memorial Hall's to restyle — if she
     * approves flat, that is a change to the MASTER chassis and Farndale
     * changes with it.
     *
     * So the default below is UNCHANGED and stays glossy. The flat version is
     * opt-in: `--nl-addortell-gloss` resolves to the existing token unless
     * something sets it, and `[data-nl-flat]` in styles.css sets it to none.
     * Add `?flat=1` to any URL to see it, drop the param to see it back.
     * Nothing is silently replaced, and deleting this fallback is the whole
     * of the change if she says yes.
     */
    boxShadow: "var(--nl-addortell-gloss, var(--nl-inner-light))",
  } as const;

  return (
    <div className="flex flex-wrap items-stretch justify-center gap-3">
      {/* The primary action leads. `extra` used to render first, which put
       *  "Classes" ahead of "Capture" on Entries — the thing you came to do
       *  should be the first tile. */}
      <button type="button" onClick={onAdd} data-testid={addTestId} className={`${light ? "nl-light-card " : ""}px-6 py-4 text-base uppercase tracking-wide`} style={buttonStyle}>
        {addLabel ?? `Add ${noun}`}
      </button>
      {extra?.map(e => (
        <button key={e.label} type="button" onClick={e.onPress} className={`${light ? "nl-light-card " : ""}px-6 py-4 text-base uppercase tracking-wide`} style={buttonStyle}>
          {e.label}
        </button>
      ))}
      {onTell && (
        <button type="button" onClick={onTell} className={`${light ? "nl-light-card " : ""}px-6 py-4 text-base uppercase tracking-wide`} style={buttonStyle}>
          {tellLabel}
        </button>
      )}
    </div>
  );
}

// ─── Unified search / filter grammar ─────────────────────────────────────────

/**
 * "**One underlying search/filter grammar, used by every substantial
 * collection** — People, Tasks, Entries, Results, Assets, Messages, and
 * whatever else emerges — with context-specific filters layered on top per
 * module … not a bespoke search system per page."
 *
 * So the grammar is this shape, and a module supplies its own `facets`. Tasks'
 * All/Mine/Assigned-by-me/Unassigned/Completed is one set of facets on this,
 * not its own search bar.
 *
 * The same structure is what Mira queries conversationally — "show me the
 * unassigned high-priority tasks in Field Two" resolves to a Query. That only
 * works because the facets are structured values rather than display text,
 * which is the payoff of the object model being real.
 */
export type Facet = {
  id: string;
  label: string;
  /** Single-select behaves as a segmented control; multi as toggles. */
  mode?: "single" | "multi";
  options: { id: string; label: string; count?: number }[];
};

export type Query = {
  text: string;
  /** facetId → selected option ids. */
  selected: Record<string, string[]>;
};

export const emptyQuery: Query = { text: "", selected: {} };

export type Suggestion = {
  id: string;
  /** What the person is looking for — a name, a class, a place. */
  label: string;
  /** Quiet context that disambiguates two similar labels. */
  hint?: string;
};

export function SearchFilter({
  facets = [], query, onChange, placeholder = "Search…",
  suggestions, onPickSuggestion, suggestionLimit = 6,
}: {
  facets?: Facet[];
  query: Query;
  onChange: (q: Query) => void;
  placeholder?: string;
  /**
   * Live matches for what has been typed so far (Drea, 2026-08-16:
   * "autocomplete/live-filtering"). The caller supplies them because only the
   * caller knows what its rows are; this component owns how they look and
   * behave, so People, Entries and the Map all get the same thing rather than
   * three lookalikes.
   *
   * The list below the input is ALREADY filtering as you type — that has
   * always been true. What this adds is the jump: type three letters, see the
   * name, go straight to it without reading the results.
   */
  suggestions?: Suggestion[];
  /** What tapping one does. Without it, picking simply completes the text. */
  onPickSuggestion?: (s: Suggestion) => void;
  suggestionLimit?: number;
}) {
  const [focused, setFocused] = useState(false);
  const toggle = (facet: Facet, optionId: string) => {
    const current = query.selected[facet.id] ?? [];
    const next = facet.mode === "multi"
      ? current.includes(optionId) ? current.filter(x => x !== optionId) : [...current, optionId]
      : current.includes(optionId) ? [] : [optionId];
    onChange({ ...query, selected: { ...query.selected, [facet.id]: next } });
  };

  const activeCount = Object.values(query.selected).flat().length;
  // Only once there is something to match on, and only while the field has
  // focus: a dropdown covering the list you are reading is worse than none.
  const shownSuggestions = focused && query.text.trim().length >= 2
    ? (suggestions ?? []).slice(0, suggestionLimit)
    : [];

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-center gap-2">
        <div
          className="flex flex-1 items-center gap-2 px-3"
          style={{
            borderRadius: "var(--nl-radius-sm)", background: "var(--nl-panel)",
            border: "1px solid var(--nl-stroke)", minHeight: "var(--nl-hit-min)",
          }}
        >
          <span aria-hidden style={{ color: "var(--nl-ink-faint)" }}>⌕</span>
          <input
            type="search"
            value={query.text}
            onChange={e => onChange({ ...query, text: e.target.value })}
            onFocus={() => setFocused(true)}
            // A tap on a suggestion would otherwise be lost to the blur that
            // unmounts it before the click lands.
            onBlur={() => window.setTimeout(() => setFocused(false), 150)}
            placeholder={placeholder}
            aria-label="Search"
            className="w-full bg-transparent py-2 text-base outline-none"
            style={{ color: "var(--nl-ink)" }}
            role="combobox"
            aria-expanded={shownSuggestions.length > 0}
            aria-autocomplete="list"
          />
        </div>
        {activeCount > 0 && (
          <button
            type="button"
            onClick={() => onChange({ ...query, selected: {} })}
            className="px-3 text-xs uppercase"
            style={{ color: "var(--nl-ink-dim)", background: "none", border: 0, cursor: "pointer", minHeight: "var(--nl-hit-min)" }}
          >
            Clear ({activeCount})
          </button>
        )}
      </div>

      {shownSuggestions.length > 0 && (
        <ul
          role="listbox" aria-label="Matches"
          className="flex flex-col overflow-hidden"
          style={{
            borderRadius: "var(--nl-radius-sm)", background: "var(--nl-panel)",
            border: "1px solid var(--nl-stroke-strong)", boxShadow: "var(--nl-shadow)",
          }}
        >
          {shownSuggestions.map(sg => (
            <li key={sg.id} role="option" aria-selected={false}>
              <button
                type="button"
                className="flex w-full flex-wrap items-baseline gap-2 px-3 py-2.5 text-left"
                style={{
                  background: "none", border: 0, cursor: "pointer",
                  color: "var(--nl-ink)", minHeight: "var(--nl-hit-min)",
                }}
                onMouseDown={e => e.preventDefault()}
                onClick={() => {
                  setFocused(false);
                  if (onPickSuggestion) onPickSuggestion(sg);
                  else onChange({ ...query, text: sg.label });
                }}
              >
                <span>{sg.label}</span>
                {sg.hint && (
                  <span className="text-xs" style={{ color: "var(--nl-ink-faint)" }}>{sg.hint}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      {facets.map(facet => (
        <div key={facet.id} className="nl-scroll-x flex items-center gap-2" role="group" aria-label={facet.label}>
          {facet.options.map(o => (
            <Chip
              key={o.id}
              selected={(query.selected[facet.id] ?? []).includes(o.id)}
              onPress={() => toggle(facet, o.id)}
            >
              {o.label}
              {o.count !== undefined && (
                <span style={{ color: "var(--nl-ink-faint)" }}> {o.count}</span>
              )}
            </Chip>
          ))}
        </div>
      ))}
    </div>
  );
}

/** Client-side application of a Query. Server-side filtering uses the same
 *  shape; this exists so small collections don't need a round trip. */
export function applyQuery<T>(
  items: T[], query: Query,
  opts: { text?: (item: T) => string; facet?: (item: T, facetId: string) => string[] }
): T[] {
  const needle = query.text.trim().toLowerCase();
  return items.filter(item => {
    if (needle && opts.text && !opts.text(item).toLowerCase().includes(needle)) return false;
    for (const [facetId, chosen] of Object.entries(query.selected)) {
      if (chosen.length === 0) continue;
      const values = opts.facet?.(item, facetId) ?? [];
      if (!chosen.some(c => values.includes(c))) return false;
    }
    return true;
  });
}

/**
 * Build a facet from whatever values the data actually holds, with real
 * counts — a section list is never a fixed taxonomy in this product ("not a
 * rigid fixed taxonomy" is the standing rule for marker categories, and
 * sections come from whatever a show's own schedule printed). Hard-coding
 * "Horticulture, Livestock, Dog Classes" would work for Farndale and be wrong
 * for the next show.
 *
 * Values are sorted by name so the strip doesn't reshuffle as counts change,
 * and an item with no value is offered as an explicit option rather than being
 * unfilterable — "Unsectioned" is exactly what somebody is looking for when
 * they want to find the rows nobody has filed.
 */
export function facetFromData<T>(
  id: string, label: string, items: T[], value: (item: T) => string | null | undefined,
  opts: { noneLabel?: string; mode?: "single" | "multi" } = {}
): Facet {
  const counts = new Map<string, number>();
  for (const item of items) {
    const v = value(item);
    const key = v && v.trim() ? v : "";
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const named = [...counts.entries()].filter(([k]) => k !== "").sort((a, b) => a[0].localeCompare(b[0]));
  const none = counts.get("") ?? 0;
  return {
    id, label, mode: opts.mode ?? "single",
    options: [
      ...named.map(([k, n]) => ({ id: k, label: k, count: n })),
      ...(none > 0 ? [{ id: "", label: opts.noneLabel ?? "None", count: none }] : []),
    ],
  };
}

// ─── Sorting ─────────────────────────────────────────────────────────────────

export type Sort = { by: string; dir: "asc" | "desc" };

/**
 * Compare two cell values for sorting.
 *
 * Two rules that matter on real show data:
 *
 *  • A MISSING VALUE SORTS LAST in both directions. Nulls are not "before
 *    everything" — a class with no number and a class numbered 1 are not
 *    neighbours, and putting the blanks at the top of a descending sort buries
 *    the rows somebody actually asked to see.
 *  • Numbers inside text compare as numbers. Real schedules use "12a" and "9",
 *    and plain string ordering puts 12a before 9, which reads as a bug to
 *    anybody holding the printed schedule.
 */
const isMissing = (v: unknown) => v === null || v === undefined || v === "";

function compareCells(a: unknown, b: unknown): number {
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

/**
 * Sort a list by one named key. Stable, and a `by` with no matching key leaves
 * the list in its server order rather than throwing — the server's order is
 * usually meaningful (newest first, time order) and is the right thing to fall
 * back to.
 */
export function applySort<T>(
  items: T[], sort: Sort | null, keys: Record<string, (item: T) => unknown>
): T[] {
  if (!sort) return items;
  const key = keys[sort.by];
  if (!key) return items;
  const dir = sort.dir === "asc" ? 1 : -1;
  return items
    .map((item, i) => ({ item, i }))
    .sort((x, y) => {
      const a = key(x.item), b = key(y.item);
      const am = isMissing(a), bm = isMissing(b);
      // Missing stays last whichever way the arrow points, so direction is
      // only ever applied to a comparison of two real values.
      if (am && bm) return x.i - y.i;
      if (am !== bm) return am ? 1 : -1;
      const c = compareCells(a, b);
      return c !== 0 ? c * dir : x.i - y.i;
    })
    .map(x => x.item);
}

/**
 * A sortable column heading. The whole heading is the control, and the current
 * column says which way it is pointing — an arrow that only appears on hover
 * is invisible on the phone half of this product.
 */
export function SortHeader({
  id, label, sort, onChange, style,
}: {
  id: string;
  label: ReactNode;
  sort: Sort | null;
  onChange: (s: Sort) => void;
  style?: React.CSSProperties;
}) {
  const active = sort?.by === id;
  const dir = active ? sort!.dir : null;
  return (
    <th className="px-2 py-2 text-left text-xs font-normal uppercase tracking-wide" style={style} aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}>
      <button
        type="button"
        onClick={() => onChange({ by: id, dir: active && dir === "asc" ? "desc" : "asc" })}
        className="inline-flex items-center gap-1 text-xs uppercase tracking-wide"
        style={{
          background: "none", border: 0, padding: 0, cursor: "pointer",
          color: active ? "var(--nl-ink)" : "var(--nl-ink-faint)",
        }}
      >
        {label}
        <span aria-hidden style={{ opacity: active ? 1 : 0.35 }}>{dir === "desc" ? "▾" : "▴"}</span>
      </button>
    </th>
  );
}

// ─── Group tags as navigation ────────────────────────────────────────────────

/**
 * "A person's role and group memberships render as small clickable tags
 * beneath their name/role on their card. Tapping a group tag navigates
 * straight to that Group's own overview page … the tag is real navigation,
 * not decorative metadata."
 *
 * `max` supports the compact card showing "a sensible number of group tags
 * plus '+N more' rather than trying to render every membership", which the
 * library added once it confirmed membership must scale well past Farndale.
 */
export function GroupTags({
  groups, max = 3, onOpenGroup, onShowAll,
}: {
  groups: { id: string; name: string }[];
  max?: number;
  onOpenGroup: (id: string) => void;
  onShowAll?: () => void;
}) {
  const shown = groups.slice(0, max);
  const hidden = groups.length - shown.length;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {shown.map(g => (
        <Chip key={g.id} onPress={() => onOpenGroup(g.id)} tone="quiet">{g.name}</Chip>
      ))}
      {hidden > 0 && (
        <Chip onPress={onShowAll} tone="quiet">+{hidden} more</Chip>
      )}
    </div>
  );
}

// ─── List rows ───────────────────────────────────────────────────────────────

/**
 * A selectable list row.
 *
 * "Selection checkboxes never trigger actions themselves, only a resulting
 * bulk-action bar does" (`stewards/overview.md`, 2026-08-08) — so ticking a box
 * here does nothing except change selection, and `BulkActionBar` is the only
 * thing that acts.
 */
/**
 * The open/closed arrow for anything that expands (Drea, 2026-10-04: "an
 * obvious collapse arrow" on Tasks and on Documents, one pattern, not two).
 *
 * ▾ closed, ▴ open: the glyph pair this product already uses (Groups). A real
 * button with the full hit size, so closing something is a target, not a
 * guess, and it says what it will do to a screen reader.
 */
export function ExpandArrow({ open, onToggle, label, testId }: {
  open: boolean;
  onToggle: () => void;
  /** What is opened or closed, for a screen reader: "task", "document". */
  label?: string;
  testId?: string;
}) {
  return (
    <button
      type="button"
      onClick={e => { e.stopPropagation(); onToggle(); }}
      aria-expanded={open}
      aria-label={`${open ? "Close" : "Open"}${label ? ` ${label}` : ""}`}
      title={open ? "Close" : "Open"}
      data-testid={testId ?? "expand-arrow"}
      data-open={open ? "true" : "false"}
      style={{
        minHeight: "var(--nl-hit-min)", minWidth: "var(--nl-hit-min)",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        borderRadius: "var(--nl-radius-sm)", border: "1px solid var(--nl-stroke)",
        background: open ? "var(--nl-band)" : "var(--nl-tile)",
        color: open ? "var(--nl-band-ink)" : "var(--nl-ink)",
        fontSize: "1.1rem", lineHeight: 1, cursor: "pointer", flexShrink: 0,
      }}
    >
      <span aria-hidden>{open ? "▴" : "▾"}</span>
    </button>
  );
}

export function ListRow({
  selected, onSelectedChange, avatar, title, subtitle, status, actions, onPress, children,
  expanded, onToggleExpanded, expandLabel,
}: {
  selected?: boolean;
  onSelectedChange?: (v: boolean) => void;
  avatar?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  status?: OperationalStatus;
  /** `icon` makes the control icon-only: the label becomes its accessible
   *  name and its tooltip, and stops taking horizontal room. Used where a row
   *  carries more actions than a phone's width can spell out. */
  actions?: { label: string; onPress: () => void; icon?: ReactNode; testId?: string }[];
  onPress?: () => void;
  children?: ReactNode;
  /** A row that opens in place: tapping it toggles, and an `ExpandArrow`
   *  shows which way it is and closes it again. Opt-in. */
  expanded?: boolean;
  onToggleExpanded?: () => void;
  expandLabel?: string;
}) {
  // An expanding row is opened by a tap anywhere on it, like any other row.
  onPress ??= onToggleExpanded;
  /**
   * The WHOLE row opens it, not just the words.
   *
   * This used to be a small button around the title, so whether a tap worked
   * depended on where in the row it landed — on the avatar, the group tags or
   * any of the padding, nothing happened. Drea found "most stewards don't
   * open, two of them do", which is exactly what an inconsistent hit area
   * looks like from the outside.
   *
   * Done on the container rather than by wrapping everything in a button,
   * because rows legitimately contain their own buttons (group tags, Assign,
   * Alert) and a button inside a button is invalid HTML that breaks keyboard
   * navigation. So the container listens, and anything that came from a real
   * control is left to that control.
   */
  const fromInteractive = (target: EventTarget | null, row: EventTarget | null) => {
    if (!(target instanceof Element)) return false;
    const control = target.closest("button, a, input, select, textarea, [role='button']");
    // THE ROW ITSELF IS NOT AN INNER CONTROL. The container carries
    // role="button" so the whole row is tappable, which meant `closest()`
    // always found the row and this guard rejected every click on it —
    // including the person's name. So tapping a name or a picture on People
    // did nothing at all (Drea, 2026-08-17, item 4), while the four action
    // buttons still worked, because they are genuine inner controls.
    return !!control && control !== row;
  };

  return (
    <div
      // Wraps on a narrow screen (found 2026-08-16 at phone width): four
      // actions plus a name do not fit across 420px, and the row was
      // truncating people's names to nothing while the buttons sat on top of
      // them. The actions drop to their own line instead of squeezing the one
      // piece of information the row exists to show.
      className="flex flex-wrap items-center gap-3 px-2 py-2"
      style={{
        borderBottom: "1px solid var(--nl-stroke)",
        cursor: onPress ? "pointer" : undefined,
        // An expanding row keeps its arrow in the TOP-RIGHT corner (Drea,
        // 2026-10-05: easier to see and reach), clear of the content.
        ...(onToggleExpanded ? { position: "relative", paddingRight: 56 } : {}),
      }}
      onClick={onPress ? e => { if (!fromInteractive(e.target, e.currentTarget)) onPress(); } : undefined}
      onKeyDown={onPress ? e => {
        if ((e.key === "Enter" || e.key === " ") && !fromInteractive(e.target, e.currentTarget)) {
          e.preventDefault();
          onPress();
        }
      } : undefined}
      role={onPress ? "button" : undefined}
      tabIndex={onPress ? 0 : undefined}
    >
      {onSelectedChange && (
        <input
          type="checkbox"
          checked={!!selected}
          onChange={e => onSelectedChange(e.target.checked)}
          aria-label="Select row"
          className="h-5 w-5 shrink-0"
        />
      )}
      {/* People are round — see the standing corner rule in theme.css.
       *
       * The picture opens the record just as the name does (Drea, 2026-08-17:
       * "check both trigger it, not just one"). It is marked as a control
       * rather than relying on the row's own click handler, so it announces
       * itself to a screen reader and cannot be broken by a future change to
       * how the row handles taps. */}
      {avatar && (
        <div
          role={onPress ? "button" : undefined}
          tabIndex={onPress ? 0 : undefined}
          aria-label={onPress ? "Open profile" : undefined}
          onClick={onPress ? e => { e.stopPropagation(); onPress(); } : undefined}
          onKeyDown={onPress ? e => {
            if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); onPress(); }
          } : undefined}
          className="shrink-0 overflow-hidden"
          style={{
            width: 56, height: 56, borderRadius: "var(--nl-avatar-radius)",
            background: "var(--nl-panel)", cursor: onPress ? "pointer" : undefined,
          }}
        >
          {avatar}
        </div>
      )}
      <div className="min-w-0 flex-1 basis-48">
        {/* Plain markup, not a button: the row itself is the control now, and
         *  nesting one inside the other is what forced the old narrow hit
         *  area in the first place. */}
        <div className="w-full min-w-0 text-left">
          <div className="flex items-center gap-2">
            {status && <StatusDot status={status} />}
            <span className="truncate text-lg" style={{ color: "var(--nl-ink)" }}>{title}</span>
          </div>
          {subtitle && <div className="truncate text-sm" style={{ color: "var(--nl-ink-dim)" }}>{subtitle}</div>}
        </div>
        {/* In an expanding row, what has been opened is read and typed in,
            so a tap on it must not count as a tap on the row and close it. */}
        {onToggleExpanded
          ? <div onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>{children}</div>
          : children}
      </div>
      {actions && actions.length > 0 && (
        <div className="ml-auto flex shrink-0 flex-wrap items-center gap-1.5">
          {actions.map(a => (
            <button
              key={a.label}
              type="button"
              onClick={a.onPress}
              data-testid={a.testId}
              aria-label={a.icon ? a.label : undefined}
              title={a.icon ? a.label : undefined}
              className={`${a.icon ? "px-2" : "px-3"} py-2 text-sm`}
              style={{
                borderRadius: "var(--nl-radius-sm)", background: "var(--nl-tile)",
                border: "1px solid var(--nl-stroke)", color: "var(--nl-ink)",
                cursor: "pointer", minHeight: "var(--nl-hit-min)", minWidth: "var(--nl-hit-min)",
                display: a.icon ? "flex" : undefined,
                alignItems: a.icon ? "center" : undefined,
                justifyContent: a.icon ? "center" : undefined,
              }}
            >
              {a.icon ?? a.label}
            </button>
          ))}
        </div>
      )}
      {onToggleExpanded && (
        <div style={{ position: "absolute", top: 6, right: 6 }}>
          <ExpandArrow open={!!expanded} onToggle={onToggleExpanded} label={expandLabel} />
        </div>
      )}
    </div>
  );
}

/** The only thing that acts on a selection. Appears when a selection exists. */
export function BulkActionBar({
  count, actions, onClear,
}: {
  count: number;
  /** `testId` is a hook for the suites — an attribute, nothing renders
   *  differently. */
  actions: { label: string; onPress: () => void; testId?: string }[];
  onClear: () => void;
}) {
  if (count === 0) return null;
  return (
    <div
      className="sticky bottom-0 flex flex-wrap items-center gap-2 px-3 py-2.5"
      style={{ background: "var(--nl-panel)", borderTop: "1px solid var(--nl-stroke-strong)", boxShadow: "var(--nl-shadow)" }}
      role="region" aria-label="Bulk actions"
    >
      <span className="text-sm" style={{ color: "var(--nl-ink)" }}>{count} selected</span>
      <div className="ml-auto flex flex-wrap items-center gap-2">
        {actions.map(a => (
          <button
            key={a.label} type="button" onClick={a.onPress}
            data-testid={a.testId}
            className="px-3 py-2 text-sm"
            style={{
              borderRadius: "var(--nl-radius-sm)", background: "var(--nl-tile)",
              border: "1px solid var(--nl-stroke)", color: "var(--nl-ink)",
              cursor: "pointer", minHeight: "var(--nl-hit-min)",
            }}
          >
            {a.label}
          </button>
        ))}
        <CloseButton label="Clear selection" onPress={onClear} />
      </div>
    </div>
  );
}

/**
 * A row of squared tabs that scrolls sideways, the selected one filled.
 *
 * The squared, filled-when-selected look of Bookings' Month / Week / Day,
 * as a shared control (Drea, 2026-10-04: Documents' category pills should be
 * "small square tiles — the same squared-tab style used elsewhere", with the
 * selected one highlighted). Real tabs, so keyboard and screen readers get
 * what the shape promises. How the row scrolls is the caller's (`className`):
 * Documents' strip carries hard-won rules about that (proof/STRIP_DRIFT.md).
 */
export function TabStrip({ tabs, selectedId, onSelect, label, className, testId, tabTestId, palette = "default" }: {
  /** `teal`: unselected tabs mid teal, the selected one light teal with dark
   *  ink (Documents, Drea 2026-10-05). `default`: Month/Week/Day's look. */
  palette?: "default" | "teal";
  tabs: { id: string; label: ReactNode; count?: ReactNode }[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  label: string;
  className?: string;
  testId?: string;
  tabTestId?: string;
}) {
  return (
    <div role="tablist" aria-label={label} className={className} data-testid={testId}
         style={{ display: "flex", gap: 4, overflowX: "auto" }}>
      {tabs.map(t => {
        const selected = t.id === selectedId;
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-current={selected ? "true" : undefined}
            data-testid={tabTestId}
            onClick={() => onSelect(t.id)}
            style={{
              flex: "0 0 auto", display: "flex", alignItems: "center", gap: 8,
              borderRadius: "var(--nl-radius-chip)",
              ...(palette === "teal" ? {
                border: `1px solid ${selected ? "var(--nl-band-stroke)" : "var(--nl-info-card-stroke)"}`,
                background: selected ? "var(--nl-band)" : "var(--nl-info-card)",
                color: selected ? "var(--nl-band-ink)" : "var(--nl-info-card-ink)",
              } : {
                border: `1px solid ${selected ? "var(--nl-stroke-strong, var(--nl-stroke))" : "var(--nl-stroke)"}`,
                background: selected ? "var(--nl-state-open, var(--nl-band))" : "var(--nl-tile)",
                color: selected ? "var(--nl-state-open-ink, var(--nl-band-ink))" : "var(--nl-ink-dim)",
              }),
              fontWeight: selected ? 600 : undefined,
              minHeight: "var(--nl-hit-min)", padding: "0 14px",
              whiteSpace: "nowrap", cursor: "pointer", font: "inherit",
            }}
          >
            <span>{t.label}</span>
            {t.count != null && t.count !== "" && (
              <span style={{ fontSize: "0.72rem", opacity: 0.8, fontVariantNumeric: "tabular-nums" }}>{t.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
